"use client";

import { useId } from "react";
import { Search } from "lucide-react";
import { Input, Select } from "@/components/forms";
import type { SelectOption } from "@/components/forms";
import { cn } from "@/lib/utils/cn";
import type { GridSearchColumn, GridSearchKind } from "./types";

export interface GridSearchProps {
  /** Searchable columns to offer in the dropdown. */
  columns: GridSearchColumn[];
  /** Currently selected column key. */
  selectedColumn: string;
  /** Fired when the user picks a different column. */
  onColumnChange: (key: string) => void;
  /** Current search text (controlled). */
  value: string;
  /** Fired on every keystroke; the parent owns any debounce. */
  onValueChange: (value: string) => void;
  disabled?: boolean;
  className?: string;
}

/** Default placeholder per kind when a column declares none of its own. */
const KIND_PLACEHOLDER: Record<GridSearchKind, string> = {
  text: "Search…",
  integer: "e.g. 12",
  currency: "e.g. $350,000",
  percentage: "e.g. 25%",
  date: "MM/DD/YYYY",
  enum: "Search…",
  composite: "Search…",
};

/**
 * Amazon-style search: a column picker joined to a value input. The dropdown
 * selects which column to search; the input searches within only that column.
 *
 * Presentational only — validation and querying happen server-side. Changing
 * the column is the parent's cue to clear the value and reset paging (see
 * `useTableSearchParams.handleSearchColumnChange`).
 */
export function GridSearch({
  columns,
  selectedColumn,
  onColumnChange,
  value,
  onValueChange,
  disabled,
  className,
}: GridSearchProps) {
  const selectId = useId();
  const options: SelectOption[] = columns.map((c) => ({
    value: c.key,
    label: c.label,
  }));

  const active =
    columns.find((c) => c.key === selectedColumn) ?? columns[0];
  const placeholder =
    active?.placeholder ?? (active ? KIND_PLACEHOLDER[active.kind] : "Search…");

  return (
    <div className={cn("flex w-full items-stretch", className)}>
      {/* Screen-reader name for the column picker (Select renders a button). */}
      <label htmlFor={selectId} className="sr-only">
        Search column
      </label>
      <div className="w-44 shrink-0">
        <Select
          id={selectId}
          selectSize="sm"
          options={options}
          value={selectedColumn}
          onChange={(v) => onColumnChange(v as string)}
          disabled={disabled}
          className="rounded-r-none focus:z-10"
        />
      </div>
      <div className="relative grow">
        <Input
          inputSize="sm"
          value={value}
          onChange={(e) => onValueChange(e.target.value)}
          placeholder={placeholder}
          leftIcon={<Search className="w-4 h-4" />}
          clearable
          disabled={disabled}
          aria-label={active ? `Search ${active.label}` : "Search"}
          className="-ml-px rounded-l-none focus:z-10"
        />
      </div>
    </div>
  );
}
